import React, { useState, useEffect, useMemo, useRef } from 'react';
import Button from '../Button/Button';
import { useAuth } from '../../contexts/AuthContext';
import { isClientRole } from '../../utils/roleUtils';
import './FiltreDeCommande.css';

/**
 * Interface des filtres - alignée avec les types de Operation
 * Les noms correspondent aux champs de l'interface Operation refactorisée
 */
interface FilterOptions {
  dateRange: {
    start: Date | null;
    end: Date | null;
  };
  lotTechnique: string;       // Correspond à lotTechniqueDisplay dans Operation
  lotGeographique: string;    // Correspond à departement dans Operation
  createur: string;           // Correspond à creePar dans Operation
  nomOperation: string;       // Correspond à nom dans Operation (renommé de nomCommande)
  entite: string;             // Correspond à organismeNom (client) OU prestataireNom (prestataire) selon le rôle
  prixRange: {
    min: number;
    max: number;
  };
  status: string;             // ID du statut
  type: string;               // BC/MS/Tous
}

interface FiltreDeCommandeProps {
  isOpen: boolean;
  onClose: () => void;
  onApplyFilters: (filters: FilterOptions) => void;
  data: any[];
  maxPrice: number;
  activeFilters?: Partial<FilterOptions>;  // Filtres actuellement actifs
}

// État initial des filtres
const getInitialFilters = (): FilterOptions => ({
  dateRange: {
    start: null,
    end: null
  },
  lotTechnique: '',
  lotGeographique: '',
  createur: '',
  nomOperation: '',
  entite: '',
  prixRange: {
    min: 0,
    max: 0  // Pas de valeur par défaut, 0 = pas de limite
  },
  status: '',
  type: ''
});

const FiltreDeCommande: React.FC<FiltreDeCommandeProps> = ({
  isOpen,
  onClose,
  onApplyFilters,
  data,
  maxPrice,
  activeFilters
}) => {
  const { user } = useAuth();
  const isAcheteur = isClientRole(user?.role || '');
  const [filters, setFilters] = useState<FilterOptions>(getInitialFilters());

  // Référence pour éviter les boucles infinies avec activeFilters
  const prevActiveFiltersRef = useRef<string>('');

  // Synchroniser avec les filtres actifs (réinitialiser quand ils sont effacés)
  useEffect(() => {
    // Sérialiser pour comparer si ça a vraiment changé
    const activeFiltersJson = JSON.stringify(activeFilters || {});

    // Ne rien faire si les filtres n'ont pas changé
    if (activeFiltersJson === prevActiveFiltersRef.current) {
      return;
    }
    prevActiveFiltersRef.current = activeFiltersJson;

    // Si activeFilters est vide ou undefined, réinitialiser le formulaire
    if (!activeFilters) {
      setFilters(getInitialFilters());
      return;
    }

    const hasActiveFilters = Object.keys(activeFilters).some(key => {
      const value = activeFilters[key as keyof FilterOptions];
      if (value === null || value === undefined || value === '') return false;
      if (typeof value === 'object') {
        if ('start' in value && 'end' in value) {
          return value.start !== null || value.end !== null;
        }
        if ('min' in value && 'max' in value) {
          return value.min > 0 || value.max > 0;
        }
      }
      return true;
    });

    if (!hasActiveFilters) {
      setFilters(getInitialFilters());
    } else {
      // Sinon, synchroniser avec les filtres actifs
      setFilters(prev => ({
        ...prev,
        ...activeFilters,
        prixRange: activeFilters.prixRange || prev.prixRange
      }));
    }
  }, [activeFilters]);

  // Extraction des valeurs uniques pour les selects
  // Compatible avec Operation (champs transformés) et Commandes (champs bruts)
  const uniqueValues = useMemo(() => ({
    // Operation: lotTechniqueDisplay | Commandes: operation?.id_lot_technique (ID seulement)
    lotTechnique: Array.from(new Set(data
      .map(item => {
        // Pour Operation (transformé) : lotTechniqueDisplay contient "ID - Nom"
        if (item.lotTechniqueDisplay) return item.lotTechniqueDisplay;
        if (item.lotTechnique && typeof item.lotTechnique === 'string') return item.lotTechnique;
        // Pour Commandes : utiliser l'ID du lot technique depuis operation
        if (item.operation?.id_lot_technique) return `Lot ${item.operation.id_lot_technique}`;
        // Fallback si lot_technique est un objet avec nom
        if (item.lot_technique?.nom && item.lot_technique.nom !== 'Non défini') return item.lot_technique.nom;
        return null;
      })
      .filter((v): v is string => Boolean(v))
    )).sort(),
    // Operation: departement | Commandes: id_lot_departement
    lotGeographique: Array.from(new Set(data
      .map(item => {
        const val = item.departement || item.lotGeographique || item.id_lot_departement || item.operation?.id_lot_departement;
        return val ? String(val) : null;
      })
      .filter((v): v is string => Boolean(v))
    )).sort((a, b) => Number(a) - Number(b)),
    // Operation: creePar | Commandes: operation?.cree_par ou cree_par
    createur: Array.from(new Set(data
      .map(item => item.creePar || item.createur || item.operation?.cree_par || item.cree_par)
      .filter((v): v is string => Boolean(v))
    )).sort(),
    // Pour acheteurs: prestataireNom (entité) | Pour prestataires: organismeNom (client)
    entite: Array.from(new Set(data
      .map(item => {
        // Collecter à la fois les clients (organismes) et les prestataires (entités)
        const valClient = item.organismeNom || item.client || item.organisme_nom || item.operation?.organisme_nom;
        const valPrestataire = item.prestataireNom || item.entite?.nom || item.entite_nom;
        // Retourner la valeur qui existe et n'est pas 'Non défini' ou '-'
        const val = valPrestataire && valPrestataire !== '-' ? valPrestataire : valClient;
        return val && val !== 'Non défini' ? val : null;
      })
      .filter((v): v is string => Boolean(v))
    )).sort(),
    // status: tableau d'objets {id, nom} pour avoir l'ID dans la valeur et le nom en affichage
    status: Array.from(
      new Map(
        data
          .filter(item => item.status?.id && item.status?.nom)
          .map(item => [item.status.id, { id: item.status.id, nom: item.status.nom }])
      ).values()
    ).sort((a, b) => a.nom.localeCompare(b.nom)),
    type: ['Tous les types', 'BC', 'MS']
  }), [data]);

  // Envoie les filtres au parent sans transformation
  // La transformation en paramètres API se fait dans Operation.tsx via api.ts
  const handleApply = () => {
    onApplyFilters(filters);
  };

  const handleMinChange = (value: number) => {
    const newMin = Math.max(0, value);
    // Si max est défini et > 0, on s'assure que min ne dépasse pas max
    const max = filters.prixRange.max;
    setFilters({
      ...filters,
      prixRange: {
        ...filters.prixRange,
        min: max > 0 ? Math.min(newMin, max) : newMin
      }
    });
  };

  const handleMaxChange = (value: number) => {
    const newMax = Math.max(0, value);
    // S'assurer que max est >= min (si max > 0)
    setFilters({
      ...filters,
      prixRange: {
        ...filters.prixRange,
        max: newMax > 0 ? Math.max(newMax, filters.prixRange.min) : newMax
      }
    });
  };

  if (!isOpen) return null;

  return (
    <div className="filtre-overlay">
      <div className="filtre-modal">
        <div className="filtre-header">
          <h2>Filtrer les commandes</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="filtre-content">
          <div className="filtre-section">
            <h3>Période</h3>
            <div className="date-range">
              <div className="date-range">
                <div className="date-inputs">
                  <div className="input-group">
                    <label htmlFor="start-date">Début</label>
                    <input
                      id="start-date"
                      type="date"
                      value={filters.dateRange.start ? filters.dateRange.start.toISOString().split('T')[0] : ''}
                      onChange={(e) =>
                        setFilters({
                          ...filters,
                          dateRange: {
                            ...filters.dateRange,
                            start: e.target.value ? new Date(e.target.value) : null
                          }
                        })
                      }
                    />
                  </div>
                  <div className="input-group">
                    <label htmlFor="end-date">Fin</label>
                    <input
                      id="end-date"
                      type="date"
                      value={filters.dateRange.end ? filters.dateRange.end.toISOString().split('T')[0] : ''}
                      onChange={(e) =>
                        setFilters({
                          ...filters,
                          dateRange: {
                            ...filters.dateRange,
                            end: e.target.value ? new Date(e.target.value) : null
                          }
                        })
                      }
                    />
                  </div>
                </div>
                {/* <DatePicker
                selectsRange
                startDate={filters.dateRange.start}
                endDate={filters.dateRange.end}
                onChange={(update: [Date | null, Date | null]) =>
                  setFilters({ ...filters, dateRange: { start: update[0], end: update[1] } })
                }
                isClearable={true}
                placeholderText="Sélectionner une période"
              /> */}
              </div>
            </div>
          </div>

          <div className="filtre-section">
            <h3>Lots</h3>
            <select
              value={filters.lotTechnique}
              onChange={(e) => setFilters({ ...filters, lotTechnique: e.target.value })}
            >
              <option value="">Tous les lots techniques</option>
              {uniqueValues.lotTechnique.map(lot => (
                <option key={lot} value={lot}>{lot}</option>
              ))}
            </select>

            <select
              value={filters.lotGeographique}
              onChange={(e) => setFilters({ ...filters, lotGeographique: e.target.value })}
            >
              <option value="">Tous les départements</option>
              {uniqueValues.lotGeographique.map(dep => (
                <option key={dep} value={dep}>{dep}</option>
              ))}
            </select>
          </div>

          {/* <div className="filtre-section">
            <h3>Recherche</h3>
            <input
              type="text"
              placeholder="Nom de l'opération"
              value={filters.nomOperation}
              onChange={(e) => setFilters({ ...filters, nomOperation: e.target.value })}
            />
          </div> */}

          <div className="filtre-section">
            <h3>Prix</h3>
            <div className="price-range">
              <div className="price-range-container">
                <div className="inputs-container">
                  <div className="input-group">
                    <label htmlFor="min-price">Min</label>
                    <input
                      id="min-price"
                      type="number"
                      min={0}
                      max={maxPrice}
                      value={filters.prixRange.min}
                      onChange={(e) => handleMinChange(parseInt(e.target.value) || 0)}
                      className="price-input"
                      placeholder="0"
                    />
                  </div>
                  <div className="input-group">
                    <label htmlFor="max-price">Max</label>
                    <input
                      id="max-price"
                      type="number"
                      min={0}
                      value={filters.prixRange.max || ''}
                      onChange={(e) => handleMaxChange(parseInt(e.target.value) || 0)}
                      className="price-input"
                      placeholder="Pas de limite"
                    />
                  </div>
                </div>
                <Button
                  value="Réinitialiser"
                  variant="secondary"
                  size="small"
                  onClick={() =>
                    setFilters({
                      ...filters,
                      prixRange: { min: 0, max: 0 },
                    })
                  }
                />
              </div>
            </div>
          </div>

          <div className="filtre-section">
            <h3>Autres filtres</h3>
            <div className="autres-filtres-grid">
              <select
                value={filters.createur}
                onChange={(e) => setFilters({ ...filters, createur: e.target.value })}
              >
                <option value="">Tous les créateurs</option>
                {uniqueValues.createur.map(createur => (
                  <option key={createur} value={createur}>{createur}</option>
                ))}
              </select>

              <select
                value={filters.entite}
                onChange={(e) => setFilters({ ...filters, entite: e.target.value })}
              >
                <option value="">{isAcheteur ? 'Tous les prestataires' : 'Tous les clients'}</option>
                {uniqueValues.entite.map(entite => (
                  <option key={entite} value={entite}>{entite}</option>
                ))}
              </select>

              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <option value="">Tous les statuts</option>
                {uniqueValues.status.map(status => (
                  <option key={status.id} value={status.id}>{status.nom}</option>
                ))}
              </select>

              <select
                value={filters.type}
                onChange={(e) => setFilters({ ...filters, type: e.target.value })}
              >
                {uniqueValues.type.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="filtre-actions">
          <Button
            value="Annuler"
            variant="secondary"
            size="medium"
            onClick={onClose}
          />
          <Button
            value="Appliquer"
            variant="primary"
            size="medium"
            onClick={handleApply}
          />
        </div>
      </div>
    </div>
  );
};

export default FiltreDeCommande;